
import { Component, Mixins, Watch } from "vue-property-decorator";
import SideMenuLayout from "@/views/sidebar/side-menu-layout.vue";
import ResearchDataMixin from "@/views/sidebar/research-data/research-data.mixin";
import BaseMixin from "@/views/base/base.mixin";
import RewardsSummary from "@/views/sidebar/research-data/rewards-summary.vue";
import AssessmentSummary from "@/views/sidebar/research-data/assessment-summary.vue";
import { DoseTime, DateTimePickerChangeEvent } from "@/views/sidebar/research-data/research-data.types";
import { getColor } from "@/views/dashboard/dashboard.utils";
import moment from "moment";

type SideMenuLayoutInstance = InstanceType<typeof SideMenuLayout>;

@Component({
  components: {
    "side-menu-layout": SideMenuLayout,
    RewardsSummary,
    AssessmentSummary
  }
})
export default class ResearchDataComponent extends Mixins(ResearchDataMixin, BaseMixin) {
  activePickerIndex: number | null = null;
  rewardsSummaryShow = false;
  assessmentSummaryShow = false;
  alertVisible = false;
  loading = false;

  get currentDay(): number {
    const researchDay1 = new Date(this.researchParticipant.researchDay1);
    const diff = moment(this.pickDate).diff(moment(researchDay1, 'MM/DD/YYYY'), "days");
    return Math.min(diff, this.selectedResearchDuration);
  }

  get timeWornHours() {
    const count = [...this.watchWorn.am, ...this.watchWorn.pm]
      .filter(value => value === 1)
      .length;

    const label = count === 1 ? 'hour' : 'hours';
    return `Time Worn: Approximately ${count} ${label}`;
  }

  get watchChargingActive() {
    const count = [...this.watchCharging.am, ...this.watchCharging.pm]
      .filter(item => item.active)
      .length;

    const label = count === 1 ? 'hour' : 'hours';
    return `Charge Time: Approximately ${count} ${label}`;
  }

  get biometricCount() {
    const count = [...this.biometricRecordCount.am, ...this.biometricRecordCount.pm]
      .filter(item => item >= 5)
      .length;
    const percentage = ((count / 24) * 100).toFixed(1);

    return `Data Export: Approximately ${percentage}%`;
  }

  getExportCellStyle(value: number, index: number, isAm: boolean) {
    const isWorn = isAm ? this.watchWorn.am[index] : this.watchWorn.pm[index];
    return {
      backgroundColor: value > 0 && value < 5 ? 
        '#efbe5e' : value == 0 && isWorn ? '#f5231f' : '#D9D9D9',
      width: '8%',
      margin: '1px',
    };
  }

  get rewardsEarned() {
    return `Rewards Earned: $${this.participantPayment.totalPayment.toFixed(2)}`;
  }

  get paidBy() {
    const date = moment(this.pickDate).format("M/D/YYYY");
    return `Paid by ${this.user.firstName} ${this.user.lastName} on ${date}`;
  }

  get disablePaid() {
    const date = moment(this.pickDate).startOf('day');
    const today = moment().startOf('day');

    return date.isSameOrAfter(today);
  }

  async mounted() {
    this.loading = true;
    const client = await this.waitForClientReady();
    if (client) {
      this.researchParticipant = JSON.parse(JSON.stringify(this.$sidebar.state?.item));
      this.pickDate = new Date(this.researchParticipant.researchDay1);
      const enrollment = moment(this.pickDate, 'MM/DD/YYYY');
      const today = moment().endOf('day');
      const diff = Math.max(1, today.diff(enrollment, 'days'));
      this.pickDate = moment(this.pickDate).add(Math.min(diff, this.selectedResearchDuration), 'days').toDate();
    } else {
      console.warn("Client not ready in time.");
    }
    this.loading = false;
  }

  async fetchData() {
    const date = moment(this.pickDate).format("YYYY/MM/DD");

    const fetch = [
      this.$store.dispatch("ResearchData/fetchSowsAssessments", {
        date: date, 
        clientId: this.selectedClientDDL.value,
        participantId: this.researchParticipant.participantId,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("ResearchData/fetchDoseTimes", {
        date: date, 
        clientId: this.selectedClientDDL.value,
        participantId: this.researchParticipant.participantId,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("ResearchData/fetchWatchWorn", {
        date: date, 
        clientId: this.selectedClientDDL.value,
        participantId: this.researchParticipant.participantId,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("ResearchData/fetchWatchCharging", {
        date: date, 
        clientId: this.selectedClientDDL.value,
        participantId: this.researchParticipant.participantId,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("ResearchData/fetchBiometricRecordCount", {
        date: date, 
        clientId: this.selectedClientDDL.value,
        participantId: this.researchParticipant.participantId,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("ResearchData/calculateParticipantPayment", {
        date: date, 
        clientId: this.selectedClientDDL.value,
        participantId: this.researchParticipant.participantId,
        timezone: this.selectedClient.timezone
      }),
      this.$store.dispatch("ResearchData/fetchParticipantPaymentStatus", {
        date: date, 
        clientId: this.selectedClientDDL.value,
        participantId: this.researchParticipant.participantId
      }),
      this.$store.dispatch("ResearchData/fetchSowsChartOptions", {
        participantId: this.researchParticipant.participantId, 
        clientId: this.selectedClientDDL.value
      }),
      this.$store.dispatch("ResearchData/fetchAppointmentStatus", {
        date: date,
        participantId: this.researchParticipant.participantId, 
        clientId: this.selectedClientDDL.value
      })
    ];
    await Promise.all(fetch);
  }

  async moveTime(forward: boolean) {
    this.loading = true;
    this.pickDate = moment(this.pickDate)
      [forward ? "add" : "subtract"](1, "days")
      .toDate();
    await this.fetchData();
    this.loading = false;
  }

  getCellStyle(value: number) {
    return {
      backgroundColor: value ? '#4CAF50' : '#ccc',
      width: '8%',
      margin: '1px',
    };
  }

  getWornCellStyle(value: number) {
    return {
      backgroundColor: value ? '#629A34' : '#D9D9D9',
      width: '8%',
      margin: '1px',
    };
  }

  getSowsColor(index: number) {
    return index !== null ? { background: getColor(index, this.sowsAlertZones, true) } : {};
  }

  onOpen(index: number) {
    this.activePickerIndex = index;
  }

  async onClose(dosage: DoseTime, index: number) {
    if (!dosage.time && !dosage.dosageResponseId) {
      this.$set(dosage, 'time', 'N/A');
      return;
    }

    this.loading = true;
    const dateString = moment(this.pickDate).format('MM/DD/YYYY');
    const nowIso = moment().format('YYYY-MM-DD[T]HH:mm:ss');
    const effectiveTime = dosage.time === 'N/A'
      ? this.getStepMinutes()
      : dosage.time!;
    const dosageDate = dosage.time == null
      ? null
      : moment(`${dateString} ${effectiveTime}`, 'MM/DD/YYYY h:mm A')
          .utc()
          .format('YYYY-MM-DD[T]HH:mm:ss');

    await this.$store.dispatch('ResearchData/upsertDosageResponse', {
      dosageResponseId: dosage.dosageResponseId,
      clientId: this.selectedClientDDL.value,
      participantId: this.researchParticipant.participantId,
      dosageDate,
      doseNumber: dosage.doseNumber ?? index,
      syncDate: nowIso,
    });
    await this.fetchData();
    this.loading = false;
    this.activePickerIndex = null;
    if (this.errorMessage) {
      (this.$refs.sideMenu as SideMenuLayoutInstance).showAlert();
    }
  }

  onChange(e: DateTimePickerChangeEvent) {
    if (e.date === false) {
      this.activePickerIndex = null;
    }
  }

  async onAppointmentChange() {
    this.loading = true;
    const dateString = moment(this.pickDate).format("YYYY-MM-DD");
    await this.$store.dispatch("ResearchData/upsertAppointmentStatus", {
      id: this.appointmentStatus?.id,
      clientId: this.selectedClientDDL.value,
      participantId: this.researchParticipant.participantId,
      appointmentDate: dateString,
      noShow: this.appointmentStatus.noShow,
      utoxTaken: this.appointmentStatus.utoxTaken,
    });
    await this.fetchData();
    this.loading = false;
    if (this.errorMessage) {
      (this.$refs.sideMenu as SideMenuLayoutInstance).showAlert();
    }
  }

  async onPaymentChange() {
    this.loading = true;
    const dateString = moment(this.pickDate).format("YYYY-MM-DD");
    await this.$store.dispatch("ResearchData/upsertPaymentHistory", {
      clientId: this.selectedClientDDL.value,
      participantId: this.researchParticipant.participantId,
      targetDate: dateString,
      paid: this.paymentStatus.paid,
      paidBy: `${this.user.firstName} ${this.user.lastName}`,
      timezone: this.selectedClient.timezone,
    });
    await this.fetchData();
    this.loading = false;
    if (this.errorMessage) {
      (this.$refs.sideMenu as SideMenuLayoutInstance).showAlert();
    }
  }

  showAssessmentSummary() {
    this.assessmentSummaryShow = true;
  }

  showResearchSummary() {
    this.rewardsSummaryShow = true;
  }

  private getStepMinutes() {
    const m = moment();
    const mins = m.minutes();
    const rounded = Math.round(mins / 5) * 5;

    m.minutes(rounded).seconds(0);
    return m.format("h:mm A");
  }

  @Watch("pickDate")
  async onReload() {
    this.loading = true;
    await this.fetchData();
    this.loading = false;
  }
}
